import { useMemo } from 'react';
import { Box, Text } from '@mantine/core';

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FECA57', '#FD79A8', '#A29BFE', '#FFA502',
];

const CRAYON_COLORS = [
  '#E74C3C', '#2ECC71', '#3498DB', '#F39C12',
  '#9B59B6', '#E67E22', '#1ABC9C', '#E84393',
];

function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 11) % 2147483647;
    return (s & 0x7fffffff) / 0x7fffffff;
  };
}

function generateCrayonCircle(size, seed) {
  const rand = seededRandom(seed);
  const cx = size / 2;
  const cy = size / 2;
  const baseR = size / 2 - 8;
  const points = 60;
  const paths = [];

  // Draw 2-3 overlapping passes for crayon texture
  const passes = 2 + Math.floor(rand() * 2);
  for (let pass = 0; pass < passes; pass++) {
    const offsetX = (rand() - 0.5) * 3;
    const offsetY = (rand() - 0.5) * 3;
    let d = '';

    const pts = [];
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * Math.PI * 2;
      const wobble = (rand() - 0.5) * (size * 0.04);
      const r = baseR + wobble;
      pts.push({
        x: cx + offsetX + Math.cos(angle) * r,
        y: cy + offsetY + Math.sin(angle) * r,
      });
    }
    d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < points; i++) {
      const prev = pts[(i - 1 + points) % points];
      const curr = pts[i];
      const next = pts[(i + 1) % points];
      const next2 = pts[(i + 2) % points];
      const cp1x = curr.x + (next.x - prev.x) / 6;
      const cp1y = curr.y + (next.y - prev.y) / 6;
      const cp2x = next.x - (next2.x - curr.x) / 6;
      const cp2y = next.y - (next2.y - curr.y) / 6;
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
    }
    d += ' Z';
    paths.push({ d, opacity: 0.5 + rand() * 0.3 });
  }
  return paths;
}

function PlayerCircle({ player, index, size = 160, opacity = 1, animate = false, highlight = false, isJudge = false }) {
  const px = typeof size === 'number' ? `${size}px` : size;
  const fontSize = `${size * 0.016}rem`;

  const crayonColor = highlight ? '#FFD700' : CRAYON_COLORS[index % CRAYON_COLORS.length];
  const crayonPaths = useMemo(
    () => generateCrayonCircle(typeof size === 'number' ? size : 160, index * 1000 + 42),
    [size, index]
  );
  const strokeWidth = Math.round(size * 0.05);

  const numSize = typeof size === 'number' ? size : 160;

  return (
    <Box
      style={{
        width: px,
        height: px,
        position: 'relative',
        overflow: 'visible',
        opacity,
        transition: 'opacity 0.3s ease',
        ...(animate ? {
          animation: 'popIn 0.3s ease-out',
          animationDelay: `${index * 0.1}s`,
          animationFillMode: 'both',
        } : {}),
      }}
    >
      {/* Player name above circle */}
      <Text
        style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#222',
          fontSize,
          fontWeight: 700,
          textAlign: 'center',
          textShadow: '1px 1px 0px rgba(255,255,255,0.8)',
          whiteSpace: 'nowrap',
          marginBottom: '2px',
          maxWidth: px,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {player.name}
      </Text>
      {/* Clipped inner circle */}
      <Box
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          backgroundColor: COLORS[index % COLORS.length],
          backgroundImage: player.avatar ? `url(${player.avatar})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          overflow: 'hidden',
        }}
      />
      {/* Crayon border */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible',
        }}
        viewBox={`0 0 ${numSize} ${numSize}`}
      >
        {crayonPaths.map((path, i) => (
          <path
            key={i}
            d={path.d}
            fill="none"
            stroke={crayonColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={path.opacity}
          />
        ))}
      </svg>
      {isJudge && (
        <Box
          style={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#FFD700',
            color: '#000',
            fontWeight: 800,
            fontSize: `${size * 0.004}rem`,
            padding: `${Math.round(size * 0.01)}px ${Math.round(size * 0.05)}px`,
            borderRadius: '4px',
            letterSpacing: '0.1em',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            whiteSpace: 'nowrap',
          }}
        >
          JUDGE
        </Box>
      )}
    </Box>
  );
}

export default PlayerCircle;
